import React, { useEffect, useRef, useState } from 'react';

import OnboardingLayout from '../components/OnboardingLayout/OnboardingLayout';
import StepHeader from '../components/StepHeader/StepHeader';
import { Button } from '../components/Button/Button';
import { Spinner } from '../components/Button/Button.styles';
import { ChoiceGrid, ChoiceCard } from '../components/ChoiceCard/ChoiceCard';
import { ACCEPT_ANY_IMAGE, normalizeImageFile } from '../onboardingV2.images';
import { trackImagePicked } from '../onboardingV2.analytics';
import { normalizeSpaces } from '../../../utils/dbText';
import { NICHE_SEED_COUNT, getNiche, localized, nicheSuggestions } from '../onboardingV2.niches';
import {
    SelectedCount,
    Empty,
    SelectedBlock,
    SelectedTitle,
    SelectedRow,
    CatThumb,
    PhotoAction,
    AddForm,
    SubList,
    SubChip,
    SubLink,
    SubForm,
    PhotoErrorText,
    NameButton,
    RenameInput,
    SuggestedSub,
    PickTitle,
} from './CategoryStep.styles';

const MAX_SUBS = 8;
const NAME_MAX_LENGTH = 30;

const sameLabel = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/**
 * Step 3 · categorías: starts with the categories suggested for the niche of the previous step, and the
 * merchant adds / removes / renames them and their subcategories (with suggested ones per category).
 * Each becomes a storefront category (subcategories become child collections). Optional — can skip.
 * Reflects live in the preview.
 */
const CategoryStep = ({ ctx }) => {
    const {
        t,
        isDemo,
        language,
        onboarding,
        updateOnboarding,
        analytics,
        goTo,
        trackStep,
        media,
        setCategoryPhoto,
        stepNumberOf,
        totalSteps,
    } = ctx;
    const nicheKey = onboarding.niche ? onboarding.niche.key : null;
    const niche = getNiche(nicheKey);
    const suggestions = nicheSuggestions(nicheKey, language);

    const [selected, setSelected] = useState(() =>
        (onboarding.categories || []).filter((c) => typeof c === 'object')
    );
    const [customName, setCustomName] = useState('');
    const [renaming, setRenaming] = useState(null); // { id, value }
    const [subFor, setSubFor] = useState(null); // category id whose subcategory input is open
    const [subName, setSubName] = useState('');
    const [photoBusy, setPhotoBusy] = useState(null); // category id whose photo is being converted
    const [photoError, setPhotoError] = useState(null); // { id, message }

    const fileInputRef = useRef(null);
    const activeCatId = useRef(null);

    const persist = (list) => {
        setSelected(list);
        updateOnboarding({ categories: list });
    };

    const fromSuggestion = (suggestion) => ({
        id: suggestion.id,
        label: suggestion.label,
        icon: suggestion.icon,
        suggestedSubs: suggestion.subs,
    });

    // First visit for this niche: start with its first suggestions. Coming back after changing the
    // niche replaces them only if the merchant didn't touch the list (still the untouched seed).
    useEffect(() => {
        if (!nicheKey || onboarding.categoriesSeededFor === nicheKey) return;
        const untouched =
            selected.every((c) => onboarding.seededCategoryIds && onboarding.seededCategoryIds.includes(c.id)) &&
            selected.every((c) => !(c.subcategories || []).length && !(media && media.categoryPhotos[c.id]));
        const seed = suggestions.slice(0, NICHE_SEED_COUNT).map(fromSuggestion);
        const next = untouched ? seed : selected;
        setSelected(next);
        updateOnboarding({
            categories: next,
            categoriesSeededFor: nicheKey,
            seededCategoryIds: next.map((c) => c.id),
        });
        // Once per niche.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nicheKey]);

    const isSelected = (id) => selected.some((c) => c.id === id);

    const removeCategory = (category) => {
        if (setCategoryPhoto) setCategoryPhoto(category.id, null);
        persist(selected.filter((c) => c.id !== category.id));
    };

    const toggleSuggestion = (suggestion) => {
        const existing = selected.find((c) => c.id === suggestion.id);
        if (existing) {
            removeCategory(existing);
            return;
        }
        persist([...selected, fromSuggestion(suggestion)]);
    };

    const addCustom = () => {
        const label = normalizeSpaces(customName);
        if (!label) {
            setCustomName('');
            return;
        }
        if (selected.some((c) => sameLabel(c.label, label))) {
            setCustomName('');
            return;
        }
        const id = `custom-${Date.now()}`;
        persist([...selected, { id, label, icon: 'ri-price-tag-3-line', custom: true }]);
        setCustomName(''); // keep the input ready to add several in a row
    };

    const startRename = (category) => setRenaming({ id: category.id, value: category.label });

    const commitRename = () => {
        if (!renaming) return;
        const label = normalizeSpaces(renaming.value);
        const clash = selected.some((c) => c.id !== renaming.id && sameLabel(c.label, label));
        const before = selected.find((c) => c.id === renaming.id);
        if (label && !clash && before && before.label !== label) {
            persist(selected.map((c) => (c.id === renaming.id ? { ...c, label } : c)));
        }
        setRenaming(null);
    };

    const subsOf = (category) => (Array.isArray(category.subcategories) ? category.subcategories : []);

    const addSubLabel = (categoryId, rawLabel) => {
        const label = normalizeSpaces(rawLabel);
        if (!label) return;
        persist(
            selected.map((category) => {
                if (category.id !== categoryId) return category;
                const subs = subsOf(category);
                if (subs.length >= MAX_SUBS || subs.some((s) => sameLabel(s.label, label))) return category;
                return { ...category, subcategories: [...subs, { id: `sub-${Date.now()}`, label }] };
            })
        );
    };

    const addSub = (categoryId) => {
        addSubLabel(categoryId, subName);
        setSubName(''); // keep the input open to add several in a row
    };

    const removeSub = (categoryId, subId) => {
        persist(
            selected.map((category) =>
                category.id === categoryId
                    ? { ...category, subcategories: subsOf(category).filter((s) => s.id !== subId) }
                    : category
            )
        );
    };

    const openSub = (categoryId) => {
        setSubFor(categoryId);
        setSubName('');
    };

    const openPhoto = (id) => {
        activeCatId.current = id;
        if (fileInputRef.current) fileInputRef.current.click();
    };

    // Any picture (any size, HEIC from an iPhone included) is prepared so the store can show it.
    const onPhotoChange = async (event) => {
        const file = event.target.files && event.target.files[0];
        event.target.value = '';
        const categoryId = activeCatId.current;
        if (!file || !categoryId) return;
        setPhotoError(null);
        setPhotoBusy(categoryId);
        const startedAt = Date.now();
        try {
            const ready = await normalizeImageFile(file);
            if (setCategoryPhoto) setCategoryPhoto(categoryId, ready);
            trackImagePicked(analytics, { target: 'category', file, ready, startedAt });
        } catch (error) {
            trackImagePicked(analytics, { target: 'category', file, error, startedAt });
            setPhotoError({ id: categoryId, message: t('SignUpV2.errors.photoType') });
        } finally {
            setPhotoBusy(null);
        }
    };

    const onContinue = () => {
        updateOnboarding({ categories: selected, categoryDone: true });
        if (selected.length) {
            const subcategoriesCount = selected.reduce((sum, c) => sum + subsOf(c).length, 0);
            // The step's result goes on its signup_step_completed (one event per step).
            trackStep('signup_step_completed', {
                categories_count: selected.length,
                categories: selected.map((c) => c.label).join(','),
                subcategories: selected
                    .flatMap((c) => subsOf(c).map((sub) => `${c.label} > ${sub.label}`))
                    .join(','),
                with_photo_count: selected.filter((c) => photoFor(c.id)).length,
                suggested_count: selected.filter((c) => !c.custom).length,
                custom_count: selected.filter((c) => c.custom).length,
                subcategories_count: subcategoriesCount,
                niche: nicheKey,
            });
        } else {
            trackStep('signup_step_completed', { skipped: true });
        }
        goTo('producto');
    };

    const onBack = () => {
        analytics.track('signup_step_back', { from_step: 'categorias' });
        goTo('tienda');
    };

    const photoFor = (id) => (media && media.categoryPhotos ? media.categoryPhotos[id] : null);

    return (
        <OnboardingLayout
            preview={ctx.preview}
            step={stepNumberOf('categorias')}
            total={totalSteps}
            stepKey="categorias"
            title={t('SignUpV2.categorias.title')}
            headingId="categorias-title"
            isDemo={isDemo}
            onBack={onBack}
            footer={
                <Button onClick={onContinue} iconRight="ri-arrow-right-line">
                    {selected.length
                        ? t('SignUpV2.categorias.cta', { count: selected.length })
                        : t('SignUpV2.categorias.skip')}
                </Button>
            }
        >
            <StepHeader
                id="categorias-title"
                center
                title={t('SignUpV2.categorias.title')}
                subtitle={t('SignUpV2.categorias.subtitle')}
            />

            <AddForm>
                <input
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder={t('SignUpV2.categorias.customPlaceholder')}
                    aria-label={t('SignUpV2.categorias.addCustom')}
                    maxLength={NAME_MAX_LENGTH}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            addCustom();
                        }
                    }}
                />
                <PhotoAction type="button" onClick={addCustom} disabled={!customName.trim()}>
                    <i className="ri-add-line" aria-hidden="true" /> {t('SignUpV2.categorias.add')}
                </PhotoAction>
            </AddForm>

            {selected.length === 0 && <Empty>{t('SignUpV2.categorias.empty')}</Empty>}

            {selected.length > 0 && (
                <SelectedBlock>
                    <SelectedTitle>{t('SignUpV2.categorias.yours')}</SelectedTitle>
                    <SelectedCount>{t('SignUpV2.categorias.photoHint')}</SelectedCount>
                    {selected.map((category) => {
                        const photo = photoFor(category.id);
                        const subs = subsOf(category);
                        const subOpen = subFor === category.id;
                        const isRenaming = renaming && renaming.id === category.id;
                        const pendingSuggestedSubs = (category.suggestedSubs || []).filter(
                            (label) => !subs.some((s) => sameLabel(s.label, label))
                        );
                        return (
                            <React.Fragment key={category.id}>
                                <SelectedRow>
                                    <CatThumb
                                        type="button"
                                        hasPhoto={!!photo}
                                        src={photo || undefined}
                                        onClick={() => openPhoto(category.id)}
                                        aria-label={t('SignUpV2.categorias.photoFor', {
                                            name: category.label,
                                        })}
                                    >
                                        {photoBusy === category.id ? (
                                            <Spinner className="ri-loader-4-line" aria-hidden="true" />
                                        ) : (
                                            !photo && (
                                                <i className={category.icon || 'ri-image-add-line'} aria-hidden="true" />
                                            )
                                        )}
                                    </CatThumb>
                                    {isRenaming ? (
                                        <RenameInput
                                            type="text"
                                            value={renaming.value}
                                            onChange={(e) => setRenaming({ ...renaming, value: e.target.value })}
                                            onBlur={commitRename}
                                            aria-label={t('SignUpV2.categorias.renameFor', { name: category.label })}
                                            maxLength={NAME_MAX_LENGTH}
                                            autoFocus
                                            onFocus={(e) => e.target.select()}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    commitRename();
                                                }
                                                if (e.key === 'Escape') setRenaming(null);
                                            }}
                                        />
                                    ) : (
                                        <NameButton
                                            type="button"
                                            onClick={() => startRename(category)}
                                            aria-label={t('SignUpV2.categorias.renameFor', { name: category.label })}
                                        >
                                            <span>{category.label}</span>
                                            <i className="ri-pencil-line" aria-hidden="true" />
                                        </NameButton>
                                    )}
                                    <PhotoAction type="button" onClick={() => openPhoto(category.id)}>
                                        {photo
                                            ? t('SignUpV2.categorias.changePhoto')
                                            : t('SignUpV2.categorias.addPhoto')}
                                    </PhotoAction>
                                    <PhotoAction
                                        type="button"
                                        onClick={() => removeCategory(category)}
                                        aria-label={t('SignUpV2.categorias.removeCat', {
                                            name: category.label,
                                        })}
                                    >
                                        <i className="ri-close-line" aria-hidden="true" />
                                    </PhotoAction>
                                </SelectedRow>
                                {photoError && photoError.id === category.id && (
                                    <PhotoErrorText role="alert">{photoError.message}</PhotoErrorText>
                                )}
                                <SubList>
                                    {subs.map((sub) => (
                                        <SubChip key={sub.id}>
                                            <span>{sub.label}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeSub(category.id, sub.id)}
                                                aria-label={t('SignUpV2.categorias.removeSub', {
                                                    name: sub.label,
                                                })}
                                            >
                                                <i className="ri-close-line" aria-hidden="true" />
                                            </button>
                                        </SubChip>
                                    ))}
                                    {subs.length < MAX_SUBS &&
                                        pendingSuggestedSubs.map((label) => (
                                            <SuggestedSub
                                                key={label}
                                                type="button"
                                                onClick={() => addSubLabel(category.id, label)}
                                                aria-label={t('SignUpV2.categorias.addSuggestedSub', { name: label })}
                                            >
                                                <i className="ri-add-line" aria-hidden="true" />
                                                {label}
                                            </SuggestedSub>
                                        ))}
                                    {subOpen && subs.length < MAX_SUBS && (
                                        <SubForm>
                                            <input
                                                type="text"
                                                value={subName}
                                                onChange={(e) => setSubName(e.target.value)}
                                                placeholder={t('SignUpV2.categorias.subPlaceholder')}
                                                aria-label={t('SignUpV2.categorias.subFor', {
                                                    name: category.label,
                                                })}
                                                maxLength={NAME_MAX_LENGTH}
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addSub(category.id);
                                                    }
                                                    if (e.key === 'Escape') setSubFor(null);
                                                }}
                                            />
                                            <PhotoAction
                                                type="button"
                                                onClick={() => addSub(category.id)}
                                                disabled={!subName.trim()}
                                            >
                                                {t('SignUpV2.categorias.add')}
                                            </PhotoAction>
                                            <PhotoAction
                                                type="button"
                                                onClick={() => setSubFor(null)}
                                                aria-label={t('SignUpV2.common.close')}
                                            >
                                                <i className="ri-close-line" aria-hidden="true" />
                                            </PhotoAction>
                                        </SubForm>
                                    )}
                                    {!subOpen && subs.length < MAX_SUBS && (
                                        <SubLink type="button" onClick={() => openSub(category.id)}>
                                            <i className="ri-add-line" aria-hidden="true" />
                                            {t('SignUpV2.categorias.addSub')}
                                        </SubLink>
                                    )}
                                    {subs.length >= MAX_SUBS && (
                                        <SubLink as="span" muted>
                                            {t('SignUpV2.categorias.subMax', { count: MAX_SUBS })}
                                        </SubLink>
                                    )}
                                </SubList>
                            </React.Fragment>
                        );
                    })}
                </SelectedBlock>
            )}

            {suggestions.length > 0 && (
                <>
                    <PickTitle id="categorias-suggested">
                        {t('SignUpV2.categorias.suggested', { niche: localized(niche.label, language) })}
                    </PickTitle>
                    <ChoiceGrid labelledBy="categorias-suggested">
                        {suggestions.map((suggestion) => (
                            <ChoiceCard
                                key={suggestion.id}
                                icon={suggestion.icon}
                                label={suggestion.label}
                                selected={isSelected(suggestion.id)}
                                onSelect={() => toggleSuggestion(suggestion)}
                            />
                        ))}
                    </ChoiceGrid>
                </>
            )}

            <input ref={fileInputRef} type="file" accept={ACCEPT_ANY_IMAGE} hidden onChange={onPhotoChange} />
        </OnboardingLayout>
    );
};

export default CategoryStep;
